import React, { useEffect, useMemo, useState } from "react";
import { useNavigate, Link, useSearchParams } from "react-router-dom";
import { Loader2 } from "lucide-react";
import toast from "react-hot-toast";
import { useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";

import { cn } from "@/lib/utils";
import { useSelector } from "react-redux";

import { Card } from "@/components/ui/Card";

import { acceptInviteSchema } from "./validation";

import AcceptInviteForm from "./components/AcceptInviteForm";

import { useAuth } from "@/hooks/useAuth";
import AuthService from "@/services/authService";

const AcceptInvite = () => {
    // React Router navigation
    const navigate = useNavigate();

    // Custom auth hook — logout is dispatched after a successful invite
    // accept so the user lands on /login and signs in with their new password.
    const { acceptInvite, logout } = useAuth();

    // Get query params from URL
    const [searchParams] = useSearchParams();

    /**
     * Extract values from query params
     * Example:
     * ?token=123&c=Company&email=test@gmail.com
     */
    const tokenFromQuery = useMemo(() => searchParams.get("token") || "", [searchParams]);
    const company = useMemo(() => searchParams.get("c") || "", [searchParams]);
    const email = useMemo(() => searchParams.get("email") || "", [searchParams]);

    // Get current theme from Redux store
    const currentTheme = useSelector((state) => state.theme.theme);

    // Check if dark mode is enabled
    const isDark = currentTheme === "dark";

    // Store validated token
    const [savedToken, setSavedToken] = useState("");

    // Loading state while validating invite token
    const [isValidating, setIsValidating] = useState(true);
    const [validatingError, setValidatingError] = useState('');

    // Loading state while submitting form
    const [isSubmitting, setIsSubmitting] = useState(false);

    /**
     * React Hook Form setup
     * Uses Zod schema validation
     */
    const methods = useForm({
        resolver: zodResolver(acceptInviteSchema),
        mode: "onSubmit",
        defaultValues: {
            password: "",
            confirmPassword: "",
        },
    });

    /**
     * Validate invite token
     * Called when component loads
     */
    const runValidation = async () => {
        setIsValidating(true);

        // If token is missing
        if (!tokenFromQuery) {
            toast.error("Invite token is missing or invalid");
            setSavedToken("");
            setValidatingError("Invite token is missing or invalid");
            setIsValidating(false);
            return;
        }

        try {
            // Validate token with backend
            const validation = await AuthService.validateInvite(tokenFromQuery);

            if (validation && validation.valid === false) {
                setValidatingError(validation?.message || "Invite token is invalid or expired");
                throw new Error(validation?.message || "Invite token is invalid or expired");
            }

            setSavedToken(tokenFromQuery);
        } catch (err) {
            // Show validation error
            toast.error(
                err?.response?.data?.message ||
                err?.message ||
                "Invite token is invalid or expired"
            );
            setValidatingError(
                err?.response?.data?.message ||
                err?.message ||
                "Invite token is invalid or expired"
            );

            setSavedToken("");
        } finally {
            setIsValidating(false);
        }
    };

    /**
     * Run validation whenever token changes
     */
    useEffect(() => {
        runValidation();
    }, [tokenFromQuery]);

    /**
     * Handle form submit
     */
    const onHandleSubmit = methods.handleSubmit(async (data) => {
        // Prevent submit if token is invalid
        if (!savedToken) {
            toast.error("Invite token is missing or invalid");
            return;
        }

        try {
            setIsSubmitting(true);

            const result = await acceptInvite({
                token: savedToken,
                password: data.password,
            });

            if (!result.success) {
                toast.error(
                    result?.error?.message ||
                    result?.error?.error_code ||
                    (typeof result?.error === "string"
                        ? result.error
                        : "Invalid credentials")
                );

                return;
            }

            // The backend's /invite/accept response auto-stores an access token
            // (see AuthService.acceptInvite → handleTokenResponse), which would
            // otherwise drop the user straight onto the dashboard. Logout first
            // so the redirect to /login lands them on a clean, unauthenticated
            // sign-in screen and they have to use the password they just set.
            await logout();

            toast.success("Password set successfully. Please sign in with your new password.");

            navigate("/login", { replace: true });

        } catch (err) {
            toast.error(
                err?.response?.data?.message ||
                err?.message ||
                "Unable to accept invite"
            );
        } finally {
            setIsSubmitting(false);
        }
    });

    return (
        <div className="fixed inset-0 overflow-hidden">

            {/* Background wrapper */}
            <div
                className={cn(
                    "h-full w-full flex items-center justify-center bg-cover bg-center bg-no-repeat px-4",
                    isDark
                        ? "bg-[url('/images/home_page_bg_dark.jpg')]"
                        : "bg-[url('/images/home_page_bg_light.jpg')]",
                )}
            >

                {/* Dark overlay */}
                <div
                    className="fixed inset-0 bg-black/30 backdrop-blur-sm pointer-events-none"
                    aria-hidden
                />

                {/* Main card container */}
                <div className="relative z-10 w-full max-w-[420px] sm:max-w-[460px]">
                    <Card
                        className={cn(
                            "mx-auto flex flex-col rounded-2xl border border-white/25 bg-white/10",
                            "backdrop-blur-2xl shadow-[0_20px_80px_rgba(0,0,0,0.45)]",
                            "px-4 py-4 sm:px-6 sm:py-6",
                            "max-h-[92vh] overflow-y-auto scrollbar-hide",
                        )}
                    >

                        {/* Header */}
                        <header className="mb-2 text-center">

                            {/* Small badge */}
                            <div className="inline-flex items-center rounded-full border border-white/25 bg-black/10 px-3 py-[2px] text-[10px] sm:text-[11px] font-light tracking-wide text-text-primary">
                                Accept invite
                            </div>

                            {/* Page title */}
                            <h1 className="mt-2 text-lg sm:text-xl font-medium text-text-primary">
                                Set your password
                            </h1>

                            {/* Company and email */}
                            <p className="mt-1 text-[11px] sm:text-xs text-text-primary">
                                {company
                                    ? `Invitation for ${company}`
                                    : "Complete your invitation"}

                                {email ? ` • ${email}` : ""}
                            </p>
                        </header>

                        {/* Loading state while validating token */}
                        {isValidating ? (
                            <div className="rounded-lg border border-white/25 bg-black/10 px-4 py-3 text-xs sm:text-sm text-text-primary/80 flex items-center gap-2 justify-center">
                                <Loader2 size={16} className="animate-spin" />
                                Validating invite...
                            </div>

                        ) : !savedToken ? (

                            /* Invalid token state */
                            <div className="rounded-lg border border-red-400/40 bg-red-500/10 px-4 py-3 text-xs sm:text-sm text-red-200 text-center">
                                {validatingError}
                            </div>

                        ) : (

                            /* Invite form */
                            <AcceptInviteForm
                                methods={methods}
                                onSubmit={onHandleSubmit}
                                isSubmitting={isSubmitting}
                            />
                        )}

                        {/* Back to login — the only escape hatch on this page.
                            "Back to home" was removed because it exposed the public job listing
                            before the tenant admin had completed password setup. */}
                        <div className="mt-3 text-center text-[11px] sm:text-xs text-text-primary/70">
                            <Link
                                to="/login"
                                className="text-text-primary/80 hover:text-text-primary hover:underline"
                            >
                                Back to sign in
                            </Link>
                        </div>
                    </Card>
                </div>
            </div>
        </div>
    );
};

export default AcceptInvite;